<template>
    <div class="-mx-4 -mt-3 sm:-mx-6">
        <WizardInfoAlert>
            <p class="mt-1 text-sm text-gray-500" v-html="$t('ecsp.financial_situation.info_text')"></p>
        </WizardInfoAlert>
        <div class="gap-x-4 px-4 sm:px-6">
            <div class="grid grid-cols-1 gap-y-2 divide-gray-200 py-4 sm:grid-cols-3 sm:gap-x-4 sm:gap-y-0 sm:divide-x">
                <div class="sm:pr-5">
                    <p class="text-base font-bold text-gray-700">{{ $t('ecsp.financial_situation.net_worth') }}</p>
                    <p class="text-gray-500">
                        {{ formatCurrency(storedValues.net_worth) ?? $t('ecsp.financial_situation.no_value') }}
                    </p>
                </div>
                <div class="sm:px-5">
                    <p class="text-base font-bold text-gray-700">
                        {{ $t('ecsp.financial_situation.loss_bearing_capacity') }}
                    </p>
                    <p class="text-gray-500">
                        {{
                            formatCurrency(storedValues.loss_bearing_capacity) ??
                            $t('ecsp.financial_situation.no_value')
                        }}
                    </p>
                </div>
                <div class="sm:pl-5">
                    <p class="text-base font-bold text-gray-700">
                        {{ $t('ecsp.financial_situation.annual_net_income') }}
                    </p>
                    <p class="text-gray-500">
                        {{ formatCurrency(storedValues.annual_net_income) ?? $t('ecsp.financial_situation.no_value') }}
                    </p>
                </div>
            </div>
        </div>
        <div class="border-t border-gray-200"></div>
    </div>

    <div class="pb-5 pt-4">
        <h3 class="pb-1 text-lg font-bold text-gray-700">
            {{ $t('ecsp.financial_situation.calculation_of_net_worth') }}:
        </h3>
        <div class="mt-2 overflow-hidden rounded-lg border border-gray-200">
            <div class="grid grid-cols-1 gap-x-2 gap-y-2 px-3 py-2 sm:grid-cols-3 sm:px-5">
                <div class="flex items-center text-gray-700 sm:col-span-2">
                    {{ $t('ecsp.financial_situation.annual_net_income') }}
                    <QuestionMarkCircleIcon
                        v-tooltip="$t('ecsp.financial_situation.annual_net_income_tooltip')"
                        class="ml-2 inline-block h-5 w-5 text-gray-500" />
                </div>
                <div>
                    <BaseInputMoney
                        v-model="formValues.annual_net_income"
                        name="financial_situation.annual_net_income"
                        currency="EUR"
                        label=""
                        size="lg"></BaseInputMoney>
                </div>
            </div>
            <div class="grid grid-cols-1 gap-x-2 gap-y-2 bg-gray-50 px-3 py-2 sm:grid-cols-3 sm:px-5">
                <div class="relative flex items-center text-gray-700 sm:col-span-2">
                    <PlusCircleIcon class="mr-3 w-6 text-gray-400"></PlusCircleIcon>
                    {{ $t('ecsp.financial_situation.liquid_funds') }}
                    <QuestionMarkCircleIcon
                        v-tooltip="$t('ecsp.financial_situation.liquid_funds_tooltip')"
                        class="ml-2 inline-block h-5 w-5 text-gray-500" />
                </div>
                <div>
                    <BaseInputMoney
                        v-model="formValues.liquid_funds"
                        name="financial_situation.liquid_funds"
                        currency="EUR"
                        size="lg"></BaseInputMoney>
                </div>
            </div>
            <div class="grid grid-cols-1 gap-x-2 gap-y-2 px-3 py-2 sm:grid-cols-3 sm:px-5">
                <div class="col-span-2 flex items-center text-gray-700">
                    <MinusCircleIcon class="mr-3 w-6 text-gray-400"></MinusCircleIcon>
                    {{ $t('ecsp.financial_situation.annual_financial_commitments') }}
                    <QuestionMarkCircleIcon
                        v-tooltip="$t('ecsp.financial_situation.annual_financial_commitments_tooltip')"
                        class="ml-2 inline-block h-5 w-5 text-gray-500" />
                </div>
                <div>
                    <BaseInputMoney
                        v-model="formValues.annual_financial_commitments"
                        name="financial_situation.annual_financial_commitments"
                        currency="EUR"
                        label=""
                        size="lg"></BaseInputMoney>
                </div>
            </div>
            <div
                class="grid grid-cols-1 gap-x-2 gap-y-2 border-t border-gray-300 bg-gray-50 px-3 py-2 sm:grid-cols-3 sm:px-5">
                <div class="flex items-center text-gray-700 sm:col-span-2">
                    <PauseIcon class="mr-3 w-6 rotate-90 text-gray-400"></PauseIcon>
                    {{ $t('ecsp.financial_situation.net_worth') }}
                    <QuestionMarkCircleIcon
                        v-tooltip="$t('ecsp.financial_situation.net_worth_tooltip')"
                        class="ml-2 inline-block h-5 w-5 text-gray-500" />
                </div>
                <div>
                    <BaseInputMoney
                        v-model="netAssets"
                        disabled
                        name="financial_situation.net_worth"
                        currency="EUR"
                        label=""
                        size="lg"></BaseInputMoney>
                </div>
            </div>
        </div>
        <div class="mt-4 overflow-hidden rounded-lg border border-gray-200">
            <div class="grid grid-cols-1 gap-x-2 gap-y-2 bg-gray-100 px-3 py-2 sm:grid-cols-3 sm:px-5">
                <div class="flex items-center text-gray-700 sm:col-span-2">
                    {{ $t('ecsp.financial_situation.loss_bearing_capacity') }}
                    <QuestionMarkCircleIcon
                        v-tooltip="$t('ecsp.financial_situation.loss_bearing_capacity_tooltip')"
                        class="ml-2 inline-block h-5 w-5 text-gray-500" />
                </div>
                <div>
                    <BaseInputMoney
                        v-model="lossBearingCapacity"
                        name="financial_situation.loss_bearing_capacity"
                        currency="EUR"
                        size="lg"
                        disabled></BaseInputMoney>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseInputMoney from '../../../base/BaseInputMoney.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { MinusCircleIcon, PauseIcon, PlusCircleIcon } from '@heroicons/vue/outline'
import WizardInfoAlert from '../../WizardInfoAlert.vue'
import { App } from '../../../../typings/api-enums'
import { useVModel } from '@vueuse/core'
import { QuestionMarkCircleIcon } from '@heroicons/vue/solid'
import { formatCurrency } from '../../../../utilities/helpers'

const props = defineProps<{
    modelValue: App.Services.ComplianceProfile.ECSP.Categories.EcspFinancialSituationDto
}>()

const storedValues = ref<{
    annual_net_income: number
    loss_bearing_capacity: number
    net_worth: number
}>({
    annual_net_income: null,
    loss_bearing_capacity: null,
    net_worth: null,
})

const emit = defineEmits(['update:modelValue'])
const formValues = useVModel(props, 'modelValue', emit)

onMounted(() => {
    updatedStoredValues()
})

function updatedStoredValues() {
    storedValues.value.annual_net_income = props.modelValue.annual_net_income
    storedValues.value.loss_bearing_capacity = props.modelValue.loss_bearing_capacity
    storedValues.value.net_worth = props.modelValue.net_worth
}

defineExpose({ updatedStoredValues })

const netAssets = computed(() => {
    if (
        formValues.value.annual_net_income === null &&
        formValues.value.liquid_funds === null &&
        formValues.value.annual_financial_commitments === null
    ) {
        return null
    }
    return (
        formValues.value.annual_net_income +
        formValues.value.liquid_funds -
        formValues.value.annual_financial_commitments
    )
})

watch(netAssets, () => {
    formValues.value.net_worth = netAssets.value
})

const lossBearingCapacity = computed(() => {
    if (netAssets.value === null) {
        return null
    }
    return netAssets.value * 0.1
})

watch(lossBearingCapacity, () => {
    formValues.value.loss_bearing_capacity = lossBearingCapacity.value
})
</script>
